import { useTranslation } from "react-i18next";
import { FCC } from "types";

import { Notification } from "@/components/Interface/Notification/Notification";
import { TwoColumnLayout } from "@/components/Layout/BaseLayouts/TwoColumnLayout";
import { useDisplayedSim } from "@/components/Portal/DeviceManagement/hooks/useDisplayedSim.ts";
import { partitionRoamingOptionsForDisplay } from "@/components/Portal/DeviceManagement/Roaming/roamingUtils";
import { BookedPackagesCard } from "@/components/Portal/DeviceManagement/Roaming/ShowRoamingOptions/BookedPackagesCard/BookedPackagesCard";
import { MobileAboRoamingCard } from "@/components/Portal/DeviceManagement/Roaming/ShowRoamingOptions/MobileAboRoamingCard/MobileAboRoamingCard";
import { RoamingGoodToKnow } from "@/components/Portal/DeviceManagement/Roaming/ShowRoamingOptions/RoamingGoodToKnow/RoamingGoodToKnow";
import { useRoamingInfoForDisplayedSim } from "@/components/Portal/DeviceManagement/Roaming/useRoamingInfoForDisplayedSim";
import { useGetVoiceSettings } from "@/services/api";
import { RoamingInfoRoamingStatus } from "@/services/model";
import {
  canOrderRoaming,
  getTariffType,
  TariffTypes,
} from "@/utils/deviceUtils";

/** Displays the currently-booked roaming options for the selected sim.
 *
 * Also shows the "Add roaming option" button if there are less than 3 options booked,
 * as the user cannot book more than three options.
 *
 * If there are no options booked, it shows the "Roaming good to know" section.
 * */
export const ShowRoamingOptions: FCC = () => {
  const { data: simDetail } = useDisplayedSim();

  const orderRoamingNotAllowed = !!simDetail && !canOrderRoaming(simDetail);
  const roamingInfo = useRoamingInfoForDisplayedSim();

  const orderInProgress = roamingInfo?.some(
    (roaming) => roaming.roamingStatus === RoamingInfoRoamingStatus.ordered,
  );

  // We don't want to show not available roaming options.
  // Defaults to empty array so we show 'no roaming' content while loading.
  const roamingOptions = roamingInfo
    ? roamingInfo.filter(
        (roaming) => roaming.roamingStatus !== RoamingInfoRoamingStatus.na,
      )
    : [];

  const { mobileAboPackage, travelPackages } =
    partitionRoamingOptionsForDisplay(roamingOptions, simDetail?.serviceID);
  const hasMobileAboRoaming = !!mobileAboPackage;

  const { data: voiceSettings } = useGetVoiceSettings(
    { simId: simDetail?.simId ?? -1 },
    { query: { enabled: !!simDetail?.simId } },
  );
  const tariffType = simDetail ? getTariffType(simDetail.serviceID) : null;
  const hasRoamingCalls =
    tariffType === TariffTypes.flatMobile ||
    tariffType === TariffTypes.flatMobilePlus ||
    tariffType === TariffTypes.flatMobileSwiss;
  const isRoamingCallsUsageNotAllowed =
    voiceSettings?.reason?.includes("CreditClassC");
  const showRoamingCallsMinuteHint =
    hasRoamingCalls && !isRoamingCallsUsageNotAllowed;

  if (!simDetail) {
    return;
  }

  return (
    <div className="flex flex-col gap-6">
      <TwoColumnLayout
        rows={[
          ...(hasMobileAboRoaming && mobileAboPackage
            ? [
                {
                  left: (
                    <MobileAboRoamingCard
                      roamingInfo={mobileAboPackage}
                      simDetail={simDetail}
                      isFirstOfUsageZone={true}
                    />
                  ),
                  right: (
                    <MobileAboRoamingGoodToKnow
                      showMinuteAllowanceHint={showRoamingCallsMinuteHint}
                    />
                  ),
                },
              ]
            : []),
          {
            left: (
              <BookedPackagesCard
                roamingOptions={roamingOptions}
                travelPackages={travelPackages}
                hasMobileAboRoaming={hasMobileAboRoaming}
                simDetail={simDetail}
                bookingHref={`../booking?simId=${simDetail?.simId}`}
                disabled={orderInProgress || orderRoamingNotAllowed}
              />
            ),
            right: <RoamingGoodToKnow />,
          },
        ]}
      />
    </div>
  );
};

export const MobileAboRoamingGoodToKnow = ({
  showMinuteAllowanceHint,
}: {
  showMinuteAllowanceHint: boolean;
}) => {
  const { t } = useTranslation("portal");

  return (
    <Notification
      dataTestid="roaming-good-to-know"
      title={t("layout.goodToKnow")}
      description={
        <>
          <p>{t("mobileAbo.roamingGoodToKnow")}</p>
          {showMinuteAllowanceHint && (
            <p className="mt-4">
              {t("mobileAbo.roamingGoodToKnowMinuteAllowance")}
            </p>
          )}
        </>
      }
    />
  );
};
